import { Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { disableTenantInsight, getTenantInsightAccess, InsightLevel, InsightLevelState } from "../../service/TenantService";
import { showErrorToast, showSuccessToast } from "../../utils/Toast";
import { StyledTableCell, StyledTableRow, TableWrapper } from "../logs/logStyles";

const LEVELS: InsightLevel[] = ["smallGroups", "persons"];

// On a tenant: the extra insight it turned on itself (teams of 2-4 / per person) - when the employees were
// informed, from when it applies, whether the 6-week notice was waived, who accepted. Superadmin can turn a level
// off; the API asks for a reason (428), which the support-mode dialog collects and writes to the audit log.
export default function TenantInsightAccess({ tenantId, canEdit }: { tenantId: string; canEdit: boolean }) {
  const [t] = useTranslation("global");
  const [levels, setLevels] = useState<Partial<Record<InsightLevel, InsightLevelState>> | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => getTenantInsightAccess(tenantId).then(setLevels).catch(() => setLevels({}));
  useEffect(() => {
    if (tenantId) load();
  }, [tenantId]);

  const day = (value?: string | null) => (value ? new Date(value).toLocaleDateString("da-DK") : "-");
  const stamp = (value?: string | null) => (value ? new Date(value).toLocaleString("da-DK") : "-");
  const state = (level?: InsightLevelState) => {
    if (!level?.enabled) return t("insight.off");
    return level.effectiveAt && new Date(level.effectiveAt) <= new Date() ? t("insight.active") : t("insight.pending");
  };

  const turnOff = async (level: InsightLevel) => {
    setBusy(true);
    try {
      await disableTenantInsight(tenantId, level);
      showSuccessToast(t("insight.turnedOff"), false);
      await load();
    } catch (error: any) {
      // Cancelled reason prompt (428) or another problem
      if (error?.response?.status !== 428) showErrorToast(t("insight.failed"), false);
    } finally {
      setBusy(false);
    }
  };

  if (!levels) return null;
  return (
    <Box sx={{ marginBottom: "2%" }}>
      <Typography variant="h6" sx={{ width: "100%", mt: 2 }}>
        {t("insight.title")}
      </Typography>
      <Typography variant="body2" sx={{ width: "100%", mb: 1 }}>
        {t("insight.intro")}
      </Typography>
      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("insight.level")}</StyledTableCell>
              <StyledTableCell>{t("insight.state")}</StyledTableCell>
              <StyledTableCell>{t("insight.informedAt")}</StyledTableCell>
              <StyledTableCell>{t("insight.effectiveAt")}</StyledTableCell>
              <StyledTableCell>{t("insight.waivedNotice")}</StyledTableCell>
              <StyledTableCell>{t("insight.acceptedAt")}</StyledTableCell>
              <StyledTableCell>{t("insight.disabled")}</StyledTableCell>
              {canEdit && <StyledTableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {LEVELS.map((key) => {
              const level = levels[key];
              return (
                <StyledTableRow key={key} data-testid={`insight-row-${key}`}>
                  <TableCell>{t(`insight.levels.${key}`)}</TableCell>
                  <TableCell>{state(level)}</TableCell>
                  <TableCell>{day(level?.informedAt)}</TableCell>
                  <TableCell>{day(level?.effectiveAt)}</TableCell>
                  <TableCell>{level?.waivedNotice ? t("insight.yes") : "-"}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {level?.acceptedAt ? `${stamp(level.acceptedAt)} · v${level.version || "?"}` : "-"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {level?.disabledAt ? `${stamp(level.disabledAt)} (${t(`insight.by.${level.disabledByRole || "tenant"}`)})` : "-"}
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      {level?.enabled && (
                        <Button size="small" variant="outlined" color="error" disabled={busy} onClick={() => turnOff(key)} data-testid={`insight-off-${key}`}>
                          {t("insight.turnOff")}
                        </Button>
                      )}
                    </TableCell>
                  )}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableWrapper>
    </Box>
  );
}
